import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconArrowDown, IconArrowUp, IconArrowsSort, IconPlus, IconSearch, IconX } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { Batch, ProductType } from "../api/types";
import { antennaErrorMessage } from "../lib/antennaErrors";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";

type BatchSortKey = "code" | "supplier_name" | "model" | "received_date" | "device_count";
type SortDir = "asc" | "desc";

export function Batches() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const canEdit = user.role === "admin" || user.role === "operator";

  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortKey, setSortKey] = useState<BatchSortKey>("received_date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  async function load(q: string) {
    setLoading(true);
    try {
      setBatches(await api.listBatches({ q: q || undefined }));
    } catch {
      notifications.show({ color: "red", title: t("common.error"), message: t("batches.loadFailed") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const tm = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(tm);
  }, [search]);

  useEffect(() => {
    load(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  function toggleSort(key: BatchSortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "received_date" || key === "device_count" ? "desc" : "asc");
    }
  }

  const batchLink = (id: number) =>
    debouncedSearch ? `/batches/${id}?q=${encodeURIComponent(debouncedSearch)}` : `/batches/${id}`;

  const displayedRows = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const sortable = [...batches];
    sortable.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "code": av = a.code; bv = b.code; break;
        case "supplier_name": av = a.supplier_name ?? ""; bv = b.supplier_name ?? ""; break;
        case "model": av = a.model ?? ""; bv = b.model ?? ""; break;
        case "received_date": av = a.received_date ?? ""; bv = b.received_date ?? ""; break;
        case "device_count": av = a.device_count; bv = b.device_count; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sortable;
  }, [batches, sortKey, sortDir]);

  function SortHeader({ k, label }: { k: BatchSortKey; label: string }) {
    const isActive = sortKey === k;
    const Icon = isActive ? (sortDir === "asc" ? IconArrowUp : IconArrowDown) : IconArrowsSort;
    return (
      <UnstyledButton
        onClick={() => toggleSort(k)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: 600 }}
      >
        {label}
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.4 }} />
      </UnstyledButton>
    );
  }

  return (
    <Container size="xl">
      <Stack mb="md" gap="sm">
        <Title order={2}>{t("batches.title")}</Title>
        {canEdit && (
          <Group gap="xs" wrap="wrap">
            <Button leftSection={<IconPlus size={16} />} onClick={() => setModalOpen(true)}>
              {t("batches.add")}
            </Button>
          </Group>
        )}
      </Stack>

      <TextInput
        mb="md"
        placeholder={t("batches.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        rightSection={
          search ? (
            <ActionIcon variant="subtle" color="gray" onClick={() => setSearch("")}>
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
      />

      <ResponsiveTable
        mobile={{
          rows: displayedRows,
          sort: {
            options: [
              { value: "code", label: t("batches.colCode") },
              { value: "supplier_name", label: t("batches.colSupplier") },
              { value: "model", label: t("batches.colModel") },
              { value: "received_date", label: t("batches.colReceived") },
              { value: "device_count", label: t("batches.colDevices") },
            ],
            value: sortKey,
            dir: sortDir,
            onChange: (v, d) => (v === sortKey ? setSortDir(d) : toggleSort(v as BatchSortKey)),
          },
          rowKey: (b) => b.id,
          title: (b) => <Text span size="sm" fw={600}>{b.code}</Text>,
          subtitle: (b) => b.received_date ?? undefined,
          badge: (b) => <Badge variant="light">{b.device_count}</Badge>,
          fields: [
            { label: t("batches.colSupplier"), render: (b) => b.supplier_name ?? t("common.none") },
            { label: t("batches.colModel"), render: (b) => b.model ?? t("common.none") },
            {
              label: t("antennas.typeModule") + " / " + t("antennas.typeAntenna"),
              render: (b) => b.product_type_id === 2
                ? <Badge size="sm" variant="light" color="grape">{t("antennas.typeAntenna")}</Badge>
                : <Badge size="sm" variant="light" color="gray">{t("antennas.typeModule")}</Badge>,
            },
          ],
          onOpen: (b) => navigate(batchLink(b.id)),
          loading: loading && batches.length === 0,
          empty: debouncedSearch ? t("batches.emptySearch", { q: debouncedSearch }) : t("batches.empty"),
        }}
      >
      <Table.ScrollContainer minWidth={640}>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th><SortHeader k="code" label={t("batches.colCode")} /></Table.Th>
            <Table.Th><SortHeader k="supplier_name" label={t("batches.colSupplier")} /></Table.Th>
            <Table.Th><SortHeader k="model" label={t("batches.colModel")} /></Table.Th>
            <Table.Th><SortHeader k="received_date" label={t("batches.colReceived")} /></Table.Th>
            <Table.Th><SortHeader k="device_count" label={t("batches.colDevices")} /></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text c="dimmed">{t("common.loading")}</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && batches.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text c="dimmed">
                  {debouncedSearch
                    ? t("batches.emptySearch", { q: debouncedSearch })
                    : t("batches.empty")}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {displayedRows.map((b) => {
            const linkTarget = batchLink(b.id);
            return (
            <Table.Tr key={b.id}>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <Anchor component={Link} to={linkTarget} fw={500}>
                    {b.code}
                  </Anchor>
                  {b.product_type_id === 2 ? (
                    <Badge size="sm" variant="light" color="grape">{t("antennas.typeAntenna")}</Badge>
                  ) : (
                    <Badge size="sm" variant="light" color="gray">{t("antennas.typeModule")}</Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>{b.supplier_name ?? t("common.none")}</Table.Td>
              <Table.Td>{b.model ?? t("common.none")}</Table.Td>
              <Table.Td>{b.received_date ?? t("common.none")}</Table.Td>
              <Table.Td>
                <Badge variant="light">{b.device_count}</Badge>
              </Table.Td>
            </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      </ResponsiveTable>

      <CreateBatchModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => {
          setModalOpen(false);
          load(debouncedSearch);
        }}
      />
    </Container>
  );
}

interface ModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateBatchModal({ opened, onClose, onCreated }: ModalProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [typeId, setTypeId] = useState<string>("1");
  const [types, setTypes] = useState<ProductType[]>([]);
  const form = useForm({
    initialValues: {
      code: "",
      supplier_name: "",
      model: "",
      firmware_version: "",
      received_date: "",
      notes: "",
    },
    validate: {
      code: (v) => (v.trim().length === 0 ? t("batches.codeRequired") : null),
    },
  });

  useEffect(() => {
    if (opened) {
      form.reset();
      setTypeId("1");
      api.listProductTypes().then((r) => setTypes(r.types.filter((x) => x.active))).catch(() => setTypes([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  async function handleSubmit(values: typeof form.values) {
    setSubmitting(true);
    try {
      await api.createBatch({ ...values, product_type_id: Number(typeId) });
      notifications.show({ color: "green", message: t("batches.createdToast") });
      onCreated();
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      notifications.show({
        color: "red",
        title: t("common.error"),
        message:
          status === 409
            ? t("batches.codeExists")
            : (antennaErrorMessage(err, t) ?? t("batches.createFailed")),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("batches.createTitle")} size="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Select
            label={t("antennas.productType")}
            data={(types.length ? types : [{ id: 1, name: t("antennas.typeModule") }]).map((x) => ({
              value: String(x.id),
              label: x.name,
            }))}
            value={typeId}
            onChange={(v) => setTypeId(v ?? "1")}
            allowDeselect={false}
          />
          <TextInput
            label={t("batches.codeLabel")}
            required
            placeholder="LOT-2026-05-001"
            {...form.getInputProps("code")}
          />
          <TextInput label={t("batches.supplierLabel")} {...form.getInputProps("supplier_name")} />
          <TextInput label={t("batches.modelLabel")} {...form.getInputProps("model")} />
          <TextInput label={t("batches.firmwareLabel")} {...form.getInputProps("firmware_version")} />
          <TextInput
            label={t("batches.receivedLabel")}
            placeholder="YYYY-MM-DD"
            {...form.getInputProps("received_date")}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={submitting}>
              {t("common.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
